import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, Divider, FormControl, InputLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Stepper, Step, StepLabel, Chip, Stack, Autocomplete, FormHelperText,
  Avatar, CardHeader, InputAdornment, LinearProgress
} from "@mui/material";
import { 
  ArrowBack, Add, Remove, PhotoCamera, CheckCircle, LocalShipping, Business, Delete, Save,
  Inventory, CalendarToday, Schedule, Description, CloudUpload
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  getLinenVendors,
  getLinenItems,
  createLinenDispatch,
  uploadDispatchImages
} from "../../services/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export const LinenDispatch = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [linenItems, setLinenItems] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [pickupDate, setPickupDate] = useState(dayjs());
  const [pickupTime, setPickupTime] = useState(dayjs());
  const [bagCount, setBagCount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dispatchItems, setDispatchItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);

  const steps = ["Select Vendor & Date", "Add Items", "Review & Confirm"];

  // Load vendors and linen items on component mount
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [vendorsRes, itemsRes] = await Promise.all([
          getLinenVendors({ status: "active" }),
          getLinenItems({ status: "active" })
        ]);

        setVendors(vendorsRes.data?.vendors || []);
        setLinenItems(itemsRes.data?.items || itemsRes.data || []);
      } catch (err) {
        console.error("Load data error:", err);
        enqueueSnackbar("❌ Unable to load required data", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [enqueueSnackbar]);

  // Add new item to dispatch
  const addItem = () => {
    if (linenItems.length === 0) return;

    const newItem = {
      _id: `temp-${Date.now()}`,
      linen: linenItems[0],
      quantity: 1,
      weight: 0,
      availableQty: linenItems[0]?.quantity || 0
    };

    setDispatchItems([...dispatchItems, newItem]);
  };

  // Remove item from dispatch
  const removeItem = (index) => {
    const newItems = dispatchItems.filter((_, i) => i !== index);
    setDispatchItems(newItems);
  };

  // Update item properties
  const updateItem = (index, field, value) => {
    const newItems = [...dispatchItems];

    if (field === 'linen') {
      const selectedLinen = linenItems.find(item => item._id === value);
      newItems[index].linen = selectedLinen;
      newItems[index].availableQty = selectedLinen?.quantity || 0;
      // Reset quantity if it exceeds available
      if (newItems[index].quantity > newItems[index].availableQty) {
        newItems[index].quantity = newItems[index].availableQty;
      }
    } else if (field === 'quantity') {
      const qty = Math.max(0, Number(value));
      newItems[index].quantity = Math.min(qty, newItems[index].availableQty);
    } else if (field === 'weight') {
      newItems[index].weight = Math.max(0, Number(value));
    }

    setDispatchItems(newItems);
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos(files);
  };

  const validateStep1 = () => {
    if (!selectedVendor) {
      enqueueSnackbar("❌ Please select a vendor", { variant: "warning" });
      return false;
    }
    if (!pickupDate) {
      enqueueSnackbar("❌ Please select pickup date", { variant: "warning" });
      return false;
    }
    if (!pickupTime) {
      enqueueSnackbar("❌ Please select pickup time", { variant: "warning" });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (dispatchItems.length === 0) {
      enqueueSnackbar("❌ Please add at least one item", { variant: "warning" });
      return false;
    }

    const invalidItems = dispatchItems.filter(item =>
      !item.linen || item.quantity <= 0 || item.quantity > item.availableQty
    );

    if (invalidItems.length > 0) {
      enqueueSnackbar("❌ Please check item quantities", { variant: "error" });
      return false;
    }

    return true;
  };

  const handleConfirmDispatch = async () => {
    setSaving(true);
    try {
      let uploadedPhotos = [];

      // Upload photos if any
      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach((file) => formData.append("images", file));

        const uploadRes = await uploadDispatchImages(formData);

        const uploadedUrls =
          uploadRes.data?.files?.map(f => f.url) ||
          uploadRes.data?.urls ||
          [];

        uploadedPhotos = uploadedUrls;
      }

      // Combine date and time using Day.js
      const pickupDateTime = pickupDate
        .set('hour', pickupTime.hour())
        .set('minute', pickupTime.minute())
        .set('second', pickupTime.second());

      const payload = {
        vendor: selectedVendor,
        pickup_time: pickupDateTime.toISOString(),
        bag_count: bagCount ? Number(bagCount) : undefined,
        remarks: remarks || undefined,
        photos: uploadedPhotos,
        items: dispatchItems.map(item => ({
          linen: item.linen._id,
          quantity: item.quantity,
          weight: item.weight || undefined,
        })),
      };

      const result = await createLinenDispatch(payload);

      if (result.data.success) {
        enqueueSnackbar("🎉 Dispatch created successfully", { variant: "success" });
        setOpenConfirm(false);
        resetForm();
      } else {
        throw new Error(result.data.message || "Dispatch creation failed");
      }
    } catch (err) {
      console.error("Dispatch error:", err);
      enqueueSnackbar(
        err.response?.data?.message || err.message || "❌ Dispatch creation failed",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedVendor("");
    setPickupDate(dayjs());
    setPickupTime(dayjs());
    setBagCount("");
    setRemarks("");
    setDispatchItems([]);
    setPhotos([]);
    setActiveStep(0);
  };

  const nextStep = () => {
    if (activeStep === 0 && !validateStep1()) return;
    if (activeStep === 1 && !validateStep2()) return;

    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Calculate totals
  const totals = dispatchItems.reduce(
    (acc, item) => {
      acc.quantity += Number(item.quantity || 0);
      acc.weight += Number(item.weight || 0);
      return acc;
    },
    { quantity: 0, weight: 0 }
  );

  const selectedVendorObj = vendors.find(v => v._id === selectedVendor);

  if (loading && activeStep === 0) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Dispatch Data...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing vendors and linen items
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
        {/* Header */}
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3
        }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <LocalShipping sx={{ fontSize: 48, color: 'white' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography 
                  variant="h2" 
                  fontWeight={800}
                  sx={{ 
                    color: 'white',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    mb: 1
                  }}
                >
                  Create Linen Dispatch
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white', 
                    opacity: 0.9,
                    fontSize: '1.3rem'
                  }}
                >
                  Send linen items to laundry vendors for processing
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton 
                    onClick={prevStep} 
                    disabled={activeStep === 0}
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'grey.100' }
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 28 }} />
                  </IconButton>
                  
                  {/* Progress Info */}
                  {activeStep > 0 && (
                    <Stack direction="row" spacing={1}>
                      {selectedVendorObj && (
                        <Chip
                          icon={<Business sx={{ fontSize: 20 }} />}
                          label={selectedVendorObj.name}
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                        />
                      )}
                      <Chip
                        icon={<Inventory sx={{ fontSize: 20 }} />}
                        label={`${dispatchItems.length} Items`}
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      />
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stepper */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel 
                    sx={{ 
                      '& .MuiStepLabel-label': { 
                        fontSize: '1.2rem', 
                        fontWeight: 600 
                      } 
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Step 1: Select Vendor & Date */}
        {activeStep === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Business sx={{ fontSize: 32, color: 'primary.main' }} />
                      <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                        Vendor & Pickup Details
                      </Typography>
                    </Box>
                  }
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50'
                  }}
                />
                <CardContent>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="medium">
                        <InputLabel sx={{ fontSize: '1.1rem' }}>Vendor *</InputLabel>
                        <Select
                          value={selectedVendor}
                          label="Vendor *"
                          onChange={(e) => setSelectedVendor(e.target.value)}
                          sx={{ fontSize: '1.1rem' }}
                          startAdornment={
                            <InputAdornment position="start">
                              <Business sx={{ color: 'primary.main', fontSize: 28 }} />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="" sx={{ fontSize: '1.1rem' }}>-- Select Vendor --</MenuItem>
                          {vendors.map(vendor => (
                            <MenuItem key={vendor._id} value={vendor._id} sx={{ fontSize: '1.1rem' }}>
                              {vendor.name}
                              {vendor.vendorCode && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
                                  ({vendor.vendorCode})
                                </Typography>
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <DatePicker
                          label="Pickup Date *"
                          value={pickupDate}
                          onChange={(newValue) => setPickupDate(newValue)}
                          slotProps={{ 
                            textField: { 
                              fullWidth: true,
                              size: "medium",
                              InputProps: {
                                sx: { fontSize: '1.1rem' },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
                                  </InputAdornment>
                                )
                              },
                              InputLabelProps: { sx: { fontSize: '1.1rem' } }
                            } 
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <TimePicker
                          label="Pickup Time *"
                          value={pickupTime}
                          onChange={(newValue) => setPickupTime(newValue)}
                          slotProps={{ 
                            textField: { 
                              fullWidth: true,
                              size: "medium",
                              InputProps: {
                                sx: { fontSize: '1.1rem' },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Schedule sx={{ color: 'primary.main', fontSize: 28 }} />
                                  </InputAdornment>
                                )
                              },
                              InputLabelProps: { sx: { fontSize: '1.1rem' } }
                            } 
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Bag Count"
                        type="number"
                        value={bagCount}
                        onChange={(e) => setBagCount(e.target.value)}
                        inputProps={{ min: 0 }}
                        size="medium"
                        InputProps={{
                          sx: { fontSize: '1.1rem' },
                          startAdornment: (
                            <InputAdornment position="start">
                              <Inventory sx={{ color: 'primary.main', fontSize: 28 }} />
                            </InputAdornment>
                          )
                        }}
                        InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Remarks"
                        multiline
                        rows={3}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Any special instructions or remarks..."
                        size="medium"
                        InputProps={{
                          sx: { fontSize: '1.1rem' },
                          startAdornment: (
                            <InputAdornment position="start">
                              <Description sx={{ color: 'primary.main', fontSize: 28 }} />
                            </InputAdornment>
                          )
                        }}
                        InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                    <Button 
                      variant="outlined" 
                      onClick={prevStep} 
                      disabled
                      sx={{ 
                        fontSize: '1.1rem',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={nextStep}
                      disabled={!selectedVendor || !pickupDate || !pickupTime}
                      sx={{ 
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Next: Add Items
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: 100
              }}>
                <CardHeader
                  title={
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                      ℹ️ Information
                    </Typography>
                  }
                />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                    Select the vendor and pickup details for this dispatch.
                  </Alert>
                  <Stack spacing={1}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Required fields are marked with *
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Only active vendors are shown
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Pickup time should be accurate for vendor coordination
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Step 2: Add Items */}
        {activeStep === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Inventory sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                          Dispatch Items
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<Add sx={{ fontSize: 28 }} />}
                        onClick={addItem}
                        disabled={linenItems.length === 0}
                        sx={{ 
                          fontSize: '1.2rem',
                          fontWeight: 600,
                          px: 3,
                          py: 1.5,
                          borderRadius: 3
                        }}
                      >
                        Add Item
                      </Button>
                    </Box>
                  }
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50'
                  }}
                />
                <CardContent>
                  {dispatchItems.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2, fontSize: '1.1rem', borderRadius: 3 }}>
                      No items added. Click "Add Item" to start adding linen items to this dispatch.
                    </Alert>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
                        <Table>
                          <TableHead sx={{ backgroundColor: 'primary.main' }}>
                            <TableRow>
                              <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                📦 Item
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                📊 Available
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                🔢 Quantity
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                ⚖️ Weight (kg)
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                🛠️ Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dispatchItems.map((item, index) => (
                              <TableRow 
                                key={item._id} 
                                hover
                                sx={{ 
                                  '&:last-child td, &:last-child th': { border: 0 },
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    transform: 'translateY(-1px)',
                                    transition: 'all 0.3s ease'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <TableCell>
                                  <FormControl fullWidth size="medium">
                                    <Autocomplete
                                      value={item.linen}
                                      onChange={(event, newValue) => updateItem(index, 'linen', newValue?._id)}
                                      options={linenItems}
                                      getOptionLabel={(option) => `${option.name} (${option.code})`}
                                      renderInput={(params) => (
                                        <TextField 
                                          {...params} 
                                          label="Select Item" 
                                          sx={{ fontSize: '1.1rem' }}
                                        />
                                      )}
                                      sx={{ fontSize: '1.1rem' }}
                                    />
                                  </FormControl>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography
                                    variant="body1"
                                    sx={{ 
                                      fontSize: '1.1rem', 
                                      fontWeight: 600,
                                      color: item.availableQty === 0 ? "error" : "success.main"
                                    }}
                                  >
                                    {item.availableQty}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                                    <IconButton
                                      size="large"
                                      onClick={() => updateItem(index, 'quantity', Math.max(0, (Number(item.quantity) || 0) - 1))}
                                      sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' }
                                      }}
                                    >
                                      <Remove sx={{ fontSize: 20 }} />
                                    </IconButton>
                                    <TextField
                                      value={item.quantity}
                                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                      type="number"
                                      sx={{ width: 100 }}
                                      inputProps={{ 
                                        min: 0, 
                                        max: item.availableQty,
                                        style: { fontSize: '1.1rem', textAlign: 'center' }
                                      }}
                                      error={item.quantity > item.availableQty}
                                    />
                                    <IconButton
                                      size="large"
                                      onClick={() => updateItem(index, 'quantity', (Number(item.quantity) || 0) + 1)}
                                      sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' }
                                      }}
                                    >
                                      <Add sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Box>
                                  {item.quantity > item.availableQty && (
                                    <FormHelperText error sx={{ fontSize: '1rem' }}>
                                      ❌ Exceeds available quantity
                                    </FormHelperText>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    value={item.weight}
                                    onChange={(e) => updateItem(index, 'weight', e.target.value)}
                                    type="number"
                                    sx={{ width: 120 }}
                                    inputProps={{ 
                                      min: 0, 
                                      step: 0.1,
                                      style: { fontSize: '1.1rem', textAlign: 'center' }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    color="error"
                                    onClick={() => removeItem(index)}
                                    sx={{ 
                                      backgroundColor: 'error.main',
                                      color: 'white',
                                      '&:hover': { backgroundColor: 'error.dark' }
                                    }}
                                  >
                                    <Delete sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Totals */}
                      {dispatchItems.length > 0 && (
                        <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
                          <CardContent>
                            <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                              📊 Summary
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                  <strong>Total Items:</strong> {dispatchItems.length}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                  <strong>Total Quantity:</strong> {totals.quantity}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                  <strong>Total Weight:</strong> {totals.weight.toFixed(2)} kg
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}

                      {/* Photo Upload */}
                      <Box sx={{ 
                        mt: 3, 
                        p: 3, 
                        border: '2px dashed', 
                        borderColor: 'primary.main', 
                        borderRadius: 3,
                        backgroundColor: 'primary.light'
                      }}>
                        <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                          📸 Upload Photos (Optional)
                        </Typography>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUpload sx={{ fontSize: 28 }} />}
                          sx={{ 
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 3
                          }}
                        >
                          Upload Photos
                          <input
                            hidden
                            accept="image/*"
                            multiple
                            type="file"
                            onChange={handlePhotosChange}
                          />
                        </Button>
                        {photos.length > 0 && (
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'success.main', mt: 2 }}>
                            ✅ {photos.length} file(s) selected
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                    <Button 
                      variant="outlined" 
                      onClick={prevStep}
                      sx={{ 
                        fontSize: '1.1rem',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={nextStep}
                      disabled={dispatchItems.length === 0}
                      sx={{ 
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Next: Review
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: 100
              }}>
                <CardHeader
                  title={
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                      ℹ️ Item Information
                    </Typography>
                  }
                />
                <CardContent>
                  {selectedVendorObj ? (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'success.light', borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                        <strong>🏢 Vendor:</strong> {selectedVendorObj.name}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>📅 Pickup:</strong> {pickupDate && pickupTime &&
                          `${pickupDate.format('DD/MM/YYYY')} at ${pickupTime.format('HH:mm')}`
                        }
                      </Typography>
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      Vendor information will appear here
                    </Alert>
                  )}

                  <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                    Ensure quantities do not exceed available stock. The system will prevent over-dispatching.
                  </Alert>
                  <Stack spacing={1}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Select items from available linen inventory
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Available quantity is shown for reference
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • Weight is optional but recommended
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Step 3: Review & Confirm */}
        {activeStep === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                <CardHeader
                  title={
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      👁️ Review Dispatch
                    </Typography>
                  }
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50'
                  }}
                />
                <CardContent>
                  <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 3 }}>
                    ⚠️ Please review all information before confirming. This will update inventory and create the dispatch.
                  </Alert>

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                            📋 Dispatch Details
                          </Typography>
                          <Stack spacing={1}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>🏢 Vendor:</strong> {selectedVendorObj?.name}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>📅 Pickup Date:</strong> {pickupDate && pickupDate.format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>⏰ Pickup Time:</strong> {pickupTime && pickupTime.format('HH:mm')}
                            </Typography>
                            {bagCount && (
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                <strong>🎒 Bag Count:</strong> {bagCount}
                              </Typography>
                            )}
                            {remarks && (
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                <strong>📝 Remarks:</strong> {remarks}
                              </Typography>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                            📊 Summary
                          </Typography>
                          <Stack spacing={1}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>📦 Total Items:</strong> {dispatchItems.length}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>🔢 Total Quantity:</strong> {totals.quantity}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>⚖️ Total Weight:</strong> {totals.weight.toFixed(2)} kg
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              <strong>📸 Photos:</strong> {photos.length}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead sx={{ backgroundColor: 'primary.main' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                            📦 Item
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                            🏷️ Code
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                            🔢 Quantity
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                            ⚖️ Weight
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dispatchItems.map(item => (
                          <TableRow 
                            key={item._id} 
                            hover
                            sx={{ 
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.3s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.linen?.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.linen?.code || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.weight || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: 'warning.light' }}>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                              📊 Totals
                            </Typography>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                              {totals.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                              {totals.weight.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {photos.length > 0 && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 2 }}>
                      <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
                        📸 Photos ({photos.length})
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {photos.length} photo(s) will be attached to this dispatch.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                    <Button 
                      variant="outlined" 
                      onClick={prevStep}
                      sx={{ 
                        fontSize: '1.1rem',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Back to Editing
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle sx={{ fontSize: 28 }} />}
                      onClick={() => setOpenConfirm(true)}
                      sx={{ 
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Create Dispatch
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: 100
              }}>
                <CardHeader
                  title={
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                      ✅ Ready to Dispatch
                    </Typography>
                  }
                />
                <CardContent>
                  <Alert severity="success" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                    🎉 All information has been validated and is ready for submission.
                  </Alert>
                  <Stack spacing={1}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • 📦 Inventory will be updated automatically
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • 🔽 Available quantities will be reduced
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • 🟡 Dirty quantities will be increased
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • 📝 Dispatch record will be created
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                      • 🔔 Vendor can be notified
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Confirmation Dialog */}
        <Dialog 
          open={openConfirm} 
          onClose={() => setOpenConfirm(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'warning.main', 
            color: 'white',
            py: 3
          }}>
            <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
              ⚠️ Confirm Linen Dispatch
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
              This action will update inventory and create the dispatch. Please confirm all details are correct.
            </Alert>
            <Box sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                  <strong>🏢 Vendor:</strong> {selectedVendorObj?.name}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                  <strong>📦 Items:</strong> {dispatchItems.length}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                  <strong>🔢 Total Quantity:</strong> {totals.quantity}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                  <strong>⚖️ Total Weight:</strong> {totals.weight.toFixed(2)} kg
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                  <strong>📸 Photos:</strong> {photos.length}
                </Typography>
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button 
              onClick={() => setOpenConfirm(false)} 
              disabled={saving}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle sx={{ fontSize: 24 }} />}
              onClick={handleConfirmDispatch}
              disabled={saving}
              color="primary"
              sx={{ 
                fontSize: '1.2rem',
                fontWeight: 600,
                px: 4,
                py: 1,
                minWidth: 200
              }}
            >
              {saving ? "Creating..." : "Confirm Dispatch"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};